import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Search,
  Briefcase,
  Plus,
  Trash2,
  X,
  Users,
  MapPin,
  Clock,
  Eye,
  EyeOff,
  Filter,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JobPostings() {
  const navigate = useNavigate();
  const [postings, setPostings] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [positionTypes, setPositionTypes] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  const [newPosting, setNewPosting] = useState({
    title: '',
    description: '',
    position_type: '',
    location: '',
    coverage_areas: '',
    employment_type: 'Full-time',
    requirements: '',
    salary_range: '',
    status: 'Active',
    is_public: true,
  });

  useEffect(() => {
    fetchMetadata();
    fetchPostings();
  }, [statusFilter, positionFilter]);

  const fetchMetadata = async () => {
    try {
      const [posTypes, empTypes] = await Promise.all([
        axios.get(`${API}/ats/position-types`),
        axios.get(`${API}/ats/employment-types`),
      ]);
      setPositionTypes(posTypes.data.position_types);
      setEmploymentTypes(empTypes.data.employment_types);
    } catch (error) {
      console.error('Failed to load metadata:', error);
    }
  };

  const fetchPostings = async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (positionFilter) params.position_type = positionFilter;
      if (search) params.search = search;
      const response = await axios.get(`${API}/ats/job-postings`, { params });
      setPostings(response.data);
    } catch (error) {
      toast.error('Failed to load job postings');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchPostings();
  };

  const handleAddPosting = async (e) => {
    e.preventDefault();
    if (!newPosting.title || !newPosting.position_type) {
      toast.error('Title and position type are required');
      return;
    }
    try {
      const payload = {
        ...newPosting,
        coverage_areas: newPosting.coverage_areas
          ? newPosting.coverage_areas.split(',').map((a) => a.trim())
          : [],
      };
      await axios.post(`${API}/ats/job-postings`, payload);
      toast.success('Job posting created successfully');
      setShowAddModal(false);
      setNewPosting({
        title: '',
        description: '',
        position_type: '',
        location: '',
        coverage_areas: '',
        employment_type: 'Full-time',
        requirements: '',
        salary_range: '',
        status: 'Active',
        is_public: true,
      });
      fetchPostings();
    } catch (error) {
      toast.error('Failed to create job posting');
    }
  };

  const handleDeletePosting = async (e, postingId, title) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await axios.delete(`${API}/ats/job-postings/${postingId}`);
      toast.success('Job posting deleted successfully');
      fetchPostings();
    } catch (error) {
      toast.error('Failed to delete job posting');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'Closed':
        return 'bg-red-100 text-red-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-semibold text-foreground tracking-tight">
            Job Postings
          </h1>
          <p className="text-muted-foreground mt-2 leading-relaxed">
            Manage job postings and view applicants
          </p>
        </div>
        <Button
          onClick={() => setShowAddModal(true)}
          className="gap-2"
          data-testid="add-posting-btn"
        >
          <Plus size={18} />
          New Posting
        </Button>
      </div>

      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="relative md:col-span-2">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              size={18}
            />
            <Input
              type="text"
              placeholder="Search by title, description, location..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="Active">Active</option>
            <option value="Paused">Paused</option>
            <option value="Closed">Closed</option>
            <option value="Draft">Draft</option>
          </select>
          <select
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={positionFilter}
            onChange={(e) => setPositionFilter(e.target.value)}
            data-testid="position-filter"
          >
            <option value="">All Positions</option>
            {positionTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Loading job postings...
          </div>
        ) : postings.length === 0 ? (
          <div className="text-center py-12">
            <Briefcase
              className="mx-auto mb-4 text-muted-foreground"
              size={48}
              strokeWidth={1.5}
            />
            <p className="text-muted-foreground">No job postings found</p>
            <Button
              className="mt-4"
              onClick={() => setShowAddModal(true)}
              data-testid="empty-add-posting"
            >
              <Plus size={16} className="mr-2" />
              Create First Posting
            </Button>
          </div>
        ) : (
          <div className="grid gap-4">
            {postings.map((posting) => (
              <Card
                key={posting.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer border"
                onClick={() => navigate(`/ats/postings/${posting.id}`)}
                data-testid={`posting-card-${posting.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-lg">{posting.title}</h3>
                      <Badge className={getStatusColor(posting.status)}>
                        {posting.status}
                      </Badge>
                      {posting.is_public ? (
                        <Eye size={16} className="text-green-600" title="Public" />
                      ) : (
                        <EyeOff size={16} className="text-gray-400" title="Private" />
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Briefcase size={14} />
                        {posting.position_type}
                      </span>
                      {posting.location && (
                        <span className="flex items-center gap-1">
                          <MapPin size={14} />
                          {posting.location}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {posting.employment_type}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={14} />
                        {posting.applicant_count || 0} applicants
                      </span>
                    </div>
                    {posting.coverage_areas && posting.coverage_areas.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {posting.coverage_areas.slice(0, 3).map((area, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {area}
                          </Badge>
                        ))}
                        {posting.coverage_areas.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{posting.coverage_areas.length - 3} more
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={(e) => handleDeletePosting(e, posting.id, posting.title)}
                    data-testid={`delete-posting-${posting.id}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </Card>

      {/* Add Job Posting Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <Card className="w-full max-w-2xl p-6 m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">Create New Job Posting</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddModal(false)}
              >
                <X size={18} />
              </Button>
            </div>
            <form onSubmit={handleAddPosting} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Title *
                </label>
                <Input
                  value={newPosting.title}
                  onChange={(e) =>
                    setNewPosting({ ...newPosting, title: e.target.value })
                  }
                  placeholder="e.g., Physical Therapist - Houston Area"
                  data-testid="new-title"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Position Type *
                  </label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newPosting.position_type}
                    onChange={(e) =>
                      setNewPosting({ ...newPosting, position_type: e.target.value })
                    }
                    data-testid="new-position-type"
                  >
                    <option value="">Select...</option>
                    {positionTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Employment Type
                  </label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newPosting.employment_type}
                    onChange={(e) =>
                      setNewPosting({ ...newPosting, employment_type: e.target.value })
                    }
                    data-testid="new-employment-type"
                  >
                    {employmentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Location
                  </label>
                  <Input
                    value={newPosting.location}
                    onChange={(e) =>
                      setNewPosting({ ...newPosting, location: e.target.value })
                    }
                    placeholder="e.g., Houston, TX"
                    data-testid="new-location"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Salary Range
                  </label>
                  <Input
                    value={newPosting.salary_range}
                    onChange={(e) =>
                      setNewPosting({ ...newPosting, salary_range: e.target.value })
                    }
                    placeholder="e.g., $75,000 - $90,000"
                    data-testid="new-salary"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Coverage Areas (comma-separated)
                </label>
                <Input
                  value={newPosting.coverage_areas}
                  onChange={(e) =>
                    setNewPosting({ ...newPosting, coverage_areas: e.target.value })
                  }
                  placeholder="e.g., Houston, Katy, Sugar Land"
                  data-testid="new-coverage-areas"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Description
                </label>
                <textarea
                  className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newPosting.description}
                  onChange={(e) =>
                    setNewPosting({ ...newPosting, description: e.target.value })
                  }
                  placeholder="Job description..."
                  data-testid="new-description"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground">
                  Requirements
                </label>
                <textarea
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={newPosting.requirements}
                  onChange={(e) =>
                    setNewPosting({ ...newPosting, requirements: e.target.value })
                  }
                  placeholder="Job requirements..."
                  data-testid="new-requirements"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">
                    Status
                  </label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={newPosting.status}
                    onChange={(e) =>
                      setNewPosting({ ...newPosting, status: e.target.value })
                    }
                    data-testid="new-status"
                  >
                    <option value="Active">Active</option>
                    <option value="Paused">Paused</option>
                    <option value="Draft">Draft</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    id="is_public"
                    checked={newPosting.is_public}
                    onChange={(e) =>
                      setNewPosting({ ...newPosting, is_public: e.target.checked })
                    }
                    className="h-4 w-4"
                    data-testid="new-is-public"
                  />
                  <label htmlFor="is_public" className="text-sm">
                    Show on public job board
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-new-posting">
                  Create Posting
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
